import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Eyebrow, Reveal } from "../components/tscm/Reveal";

export const Breadcrumb = ({ current }) => (
  <nav aria-label="Breadcrumb" className="text-[0.625rem] uppercase tracking-[0.16em] text-sub" data-testid="breadcrumb">
    <Link to="/" className="transition-colors hover:text-violet" data-testid="breadcrumb-home">
      Home
    </Link>
    <span className="mx-2 text-violet">/</span>
    <span className="text-ink">{current}</span>
  </nav>
);

export const BeforeYouCall = () => (
  <section className="border-t border-hairline py-12 sm:py-16" data-testid="before-you-call">
    <div className="site-x">
      <Reveal>
        <Eyebrow>Before you call</Eyebrow>
      </Reveal>
      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Reveal className="h-full">
          <div className="h-full border border-hairline bg-surface p-6">
            <h3 className="font-head text-[0.9375rem] font-bold">Who asks for it.</h3>
            <p className="mt-2 text-base leading-relaxed text-sub">
              Security heads, legal counsel and HR have all asked us for TSCM.
              The company decides which meetings are sensitive.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.06} className="h-full">
          <div className="h-full border border-hairline bg-surface p-6">
            <h3 className="font-head text-[0.9375rem] font-bold">What we need.</h3>
            <p className="mt-2 text-base leading-relaxed text-sub">
              At minimum, the venue and the time at which we are to attend. We
              sign client NDAs, and can do so before you share the venue or
              the time.{" "}
              <Link to="/how-we-work#confidentiality" className="text-violet transition-colors hover:text-violeth" data-testid="byc-confidentiality-link">
                Read more
              </Link>
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.12} className="h-full">
          <div className="h-full border border-hairline bg-surface p-6">
            <h3 className="font-head text-[0.9375rem] font-bold">What you receive.</h3>
            <p className="mt-2 text-base leading-relaxed text-sub">
              A written report and a verbal debrief, sealed and delivered to
              the person who engaged us. Onward distribution is your decision.{" "}
              <a
                href="/specimen-report.pdf"
                download="TSCM-Services-Specimen-Report.pdf"
                className="text-violet transition-colors hover:text-violeth"
                data-testid="byc-specimen-link"
              >
                Download a specimen report
              </a>
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.18} className="h-full">
          <div className="h-full border border-hairline bg-surface p-6">
            <h3 className="font-head text-[0.9375rem] font-bold">Why LAT.</h3>
            <p className="mt-2 text-base leading-relaxed text-sub">
              Law Abiding Technology designs and manufactures surveillance and
              security technology; TSCM Services is its corporate TSCM arm.
              Rajiv Vermani, the lead practitioner, holds the credentials
              named on the{" "}
              <Link to="/#credentials" className="text-violet transition-colors hover:text-violeth" data-testid="byc-credentials-link">
                home page
              </Link>
              .
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export const ProgrammeLinks = ({ links }) => (
  <section className="border-t border-hairline py-12 sm:py-16">
    <div className="site-x">
      <Reveal>
        <Eyebrow>The programme</Eyebrow>
      </Reveal>
      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {links.map(({ href, tag, title }) => (
          <Link
            key={title}
            to={href}
            data-testid={`programme-link-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="group flex flex-col gap-3 border border-hairline bg-surface p-6 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-violet"
          >
            <span className="text-[0.5625rem] font-semibold uppercase tracking-[0.16em] text-violet">{tag}</span>
            <span className="flex-1 font-head text-base font-bold leading-snug">{title}</span>
            <ArrowUpRight size={15} className="text-sub transition-colors group-hover:text-violet" />
          </Link>
        ))}
      </div>
    </div>
  </section>
);

export const SubpageHero = ({ tag, lines, image, imageAlt, children }) => (
  <div className="grid site-x grid-cols-1 items-start gap-12 pb-16 pt-4 lg:grid-cols-2 lg:pb-20">
    <div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.05 }}>
        <Eyebrow>{tag}</Eyebrow>
      </motion.div>
      <h1 className="mt-5 font-head font-extrabold leading-[1.08] tracking-tight h-hero">
        {lines.map((line, i) => (
          <span key={i} className="block overflow-hidden pb-1">
            <motion.span
              className="block"
              initial={{ y: "112%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.9, delay: 0.18 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
            >
              {line}
            </motion.span>
          </span>
        ))}
      </h1>
      <motion.div
        className="mt-6 max-w-md text-lg leading-relaxed text-sub"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.div>
    </div>
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
    >
      <img
        src={image}
        alt={imageAlt}
        className="h-auto w-full rounded-xl border border-hairline"
        data-testid="subpage-hero-image"
      />
    </motion.div>
  </div>
);

export const Section = ({ id, title, children, wide }) => (
  <section id={id} className="border-t border-hairline py-12 sm:py-16">
    <div className={wide ? "site-x" : "mx-auto max-w-3xl px-5"}>
      <Reveal>
        <h2 className="font-head text-2xl font-extrabold tracking-tight sm:text-3xl">{title}</h2>
      </Reveal>
      <div className="mt-8">{children}</div>
    </div>
  </section>
);

export const CoverList = ({ items }) => (
  <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
    {items.map((item, i) => (
      <li key={item} className="flex items-start gap-3 border border-hairline bg-surface p-5 text-[0.9375rem] leading-relaxed" data-testid={`cover-item-${i + 1}`}>
        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-violet" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export const NumberedList = ({ items }) => (
  <ol className="flex flex-col gap-5">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-4 text-[0.9375rem] leading-relaxed" data-testid={`numbered-item-${i + 1}`}>
        <span className="mt-0.5 text-xs font-semibold tracking-[0.16em] text-violet">
          {String(i + 1).padStart(2, "0")}
        </span>
        <span>{item}</span>
      </li>
    ))}
  </ol>
);

export const StepList = ({ steps }) => (
  <ol className="grid grid-cols-1 gap-px border border-hairline bg-hairline sm:grid-cols-2 lg:grid-cols-4">
    {steps.map(({ bold, rest }, i) => (
      <li key={i} className="bg-base-color p-6" data-testid={`step-item-${i + 1}`}>
        <span className="text-xs font-semibold tracking-[0.16em] text-violet">
          {String(i + 1).padStart(2, "0")}
        </span>
        <p className="mt-3 text-base leading-relaxed text-sub">
          <strong className="font-semibold text-ink">{bold}</strong>
          {rest}
        </p>
      </li>
    ))}
  </ol>
);

